import React from "react";
import PropTypes from "prop-types";
import { Button, makeStyles } from "@fluentui/react-components";
import { ChevronLeft16Regular, ChevronRight16Regular } from "@fluentui/react-icons";

const PREVIOUS_PAGE_LABEL = "Previous participant page";
const NEXT_PAGE_LABEL = "Next participant page";
const MOBILE_PAGINATION_MAX_WIDTH = 575;
const TABLET_PAGINATION_MAX_WIDTH = 991;
const DESKTOP_PAGINATION_MAX_WIDTH = 360;
const MOBILE_DOT_RADIUS = 1;
const WIDE_DOT_RADIUS = 2;
const PAGE_NUMBER_BUTTON_STYLE = {
  transitionProperty: "none"
};

const useStyles = makeStyles({
  root: {
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--spacingHorizontalXXS)",
    width: "100%",
    minWidth: 0,
    pointerEvents: "auto",
    whiteSpace: "nowrap"
  },
  pageNumbers: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--spacingHorizontalXXS)",
    minWidth: 0
  },
  pageNumberButton: {
    minWidth: "32px",
    paddingRight: "var(--spacingHorizontalXS)",
    paddingLeft: "var(--spacingHorizontalXS)"
  },
  navigationButton: {
    flexShrink: 0
  },
  dotButton: {
    minWidth: "24px",
    paddingRight: "var(--spacingHorizontalXXS)",
    paddingLeft: "var(--spacingHorizontalXXS)"
  }
});

function getPageNumberLabel(dotIndex, pageIndex, pageCount) {
  const displayPage = dotIndex + 1;
  const pageLabel = `participant page ${displayPage} of ${pageCount}`;

  return dotIndex === pageIndex ? `Current ${pageLabel}` : `Go to ${pageLabel}`;
}

function clampPageIndex(pageIndex, pageCount) {
  const safePageIndex = Number.isFinite(pageIndex) ? Math.max(0, Math.floor(pageIndex)) : 0;
  const safePageCount = Number.isFinite(pageCount) ? Math.max(1, Math.floor(pageCount)) : 1;

  return Math.min(safePageIndex, safePageCount - 1);
}

function getViewportWidth() {
  return typeof window === "undefined" ? 1024 : window.innerWidth || 1024;
}

function getRange(start, end) {
  if (end < start) {
    return [];
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function getHybridPaginationItems(pageIndex, pageCount, dotRadius = MOBILE_DOT_RADIUS) {
  const safeDotRadius = Math.max(1, Math.floor(dotRadius));
  const fullPageThreshold = safeDotRadius * 2 + 3;

  if (pageCount <= fullPageThreshold) {
    return getRange(1, pageCount).map(pageNumber => ({
      pageIndex: pageNumber - 1,
      type: "page"
    }));
  }

  const firstPageIndex = 0;
  const lastPageIndex = pageCount - 1;
  const visiblePageIndexes = new Set([firstPageIndex, pageIndex, lastPageIndex]);
  const previousDotPageIndexes = getRange(
    Math.max(firstPageIndex + 1, pageIndex - safeDotRadius),
    pageIndex - 1
  ).filter(dotPageIndex => !visiblePageIndexes.has(dotPageIndex));
  const nextDotPageIndexes = getRange(pageIndex + 1, Math.min(lastPageIndex - 1, pageIndex + safeDotRadius)).filter(
    dotPageIndex => !visiblePageIndexes.has(dotPageIndex)
  );
  const items = [
    {
      pageIndex: firstPageIndex,
      type: "page"
    }
  ];

  previousDotPageIndexes.forEach(dotPageIndex => {
    items.push({
      direction: "previous",
      pageIndex: dotPageIndex,
      type: "dot"
    });
  });

  if (pageIndex !== firstPageIndex && pageIndex !== lastPageIndex) {
    items.push({
      pageIndex,
      type: "page"
    });
  }

  nextDotPageIndexes.forEach(dotPageIndex => {
    items.push({
      direction: "next",
      pageIndex: dotPageIndex,
      type: "dot"
    });
  });

  items.push({
    pageIndex: lastPageIndex,
    type: "page"
  });

  return items;
}

function useViewportWidth() {
  const [viewportWidth, setViewportWidth] = React.useState(getViewportWidth);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncViewportWidth = () => {
      setViewportWidth(getViewportWidth());
    };

    syncViewportWidth();
    window.addEventListener("resize", syncViewportWidth);

    return () => {
      window.removeEventListener("resize", syncViewportWidth);
    };
  }, []);

  return viewportWidth;
}

export function ParticipantLayoutPagination({ onPageChange, pageCount, pageIndex }) {
  const styles = useStyles();
  const safePageCount = Number.isFinite(pageCount) ? Math.max(1, Math.floor(pageCount)) : 1;
  const safePageIndex = clampPageIndex(pageIndex, safePageCount);
  const displayPage = safePageIndex + 1;
  const pageLabel = `Page ${displayPage} of ${safePageCount}`;
  const viewportWidth = useViewportWidth();
  const dotRadius = viewportWidth > MOBILE_PAGINATION_MAX_WIDTH ? WIDE_DOT_RADIUS : MOBILE_DOT_RADIUS;
  const paginationStyle =
    viewportWidth > TABLET_PAGINATION_MAX_WIDTH ? { maxWidth: `${DESKTOP_PAGINATION_MAX_WIDTH}px` } : undefined;
  const isPreviousDisabled = safePageIndex <= 0;
  const isNextDisabled = safePageIndex >= safePageCount - 1;
  const paginationItems = React.useMemo(
    () => getHybridPaginationItems(safePageIndex, safePageCount, dotRadius),
    [dotRadius, safePageCount, safePageIndex]
  );
  const requestPageChange = React.useCallback(
    nextPageIndex => {
      onPageChange(clampPageIndex(nextPageIndex, safePageCount));
    },
    [onPageChange, safePageCount]
  );

  if (safePageCount <= 1) {
    return null;
  }

  return (
    <nav
      aria-label={pageLabel}
      className={styles.root}
      data-participant-layout-page={displayPage}
      data-participant-layout-page-count={safePageCount}
      data-participant-layout-pagination=""
      style={paginationStyle}
    >
      <Button
        appearance="transparent"
        aria-label={PREVIOUS_PAGE_LABEL}
        className={styles.navigationButton}
        data-participant-layout-previous-page=""
        disabled={isPreviousDisabled}
        icon={<ChevronLeft16Regular />}
        onClick={() => requestPageChange(safePageIndex - 1)}
        size="small"
      />
      <div className={styles.pageNumbers} data-participant-layout-page-numbers="">
        {paginationItems.map(item => {
          if (item.type === "dot") {
            return (
              <Button
                appearance="transparent"
                aria-label={getPageNumberLabel(item.pageIndex, safePageIndex, safePageCount)}
                className={styles.dotButton}
                data-participant-layout-page-dot-nav=""
                data-participant-layout-page-dot-nav-direction={item.direction}
                key={`${item.direction}-${item.pageIndex}`}
                onClick={() => requestPageChange(item.pageIndex)}
                size="small"
                style={PAGE_NUMBER_BUTTON_STYLE}
              >
                {"\u2022"}
              </Button>
            );
          }

          const isActive = item.pageIndex === safePageIndex;
          const pageNumber = item.pageIndex + 1;

          return (
            <Button
              appearance={isActive ? "primary" : "transparent"}
              aria-current={isActive ? "page" : undefined}
              aria-label={getPageNumberLabel(item.pageIndex, safePageIndex, safePageCount)}
              className={styles.pageNumberButton}
              data-participant-layout-page-number=""
              data-participant-layout-page-number-active={isActive ? "true" : "false"}
              key={item.pageIndex}
              onClick={isActive ? undefined : () => requestPageChange(item.pageIndex)}
              size="small"
              style={PAGE_NUMBER_BUTTON_STYLE}
            >
              {pageNumber}
            </Button>
          );
        })}
      </div>
      <Button
        appearance="transparent"
        aria-label={NEXT_PAGE_LABEL}
        className={styles.navigationButton}
        data-participant-layout-next-page=""
        disabled={isNextDisabled}
        icon={<ChevronRight16Regular />}
        iconPosition="after"
        onClick={() => requestPageChange(safePageIndex + 1)}
        size="small"
      />
    </nav>
  );
}

ParticipantLayoutPagination.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired,
  pageIndex: PropTypes.number.isRequired
};
